import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '@hooks';
import Button from '@components/Button';
import PageHeader from '@features/PageHeader';
import { IntegrationsImage } from 'assets/images/integrations';
import { AccountIntegrationsDictionary, isAccountIntegrationAvailable } from '@constants/integrations';
import useIntegrationActions from './useIntegrationActions';
import IntegrationsTable from './IntegrationsTable';
import AddIntegrationModal from './AddIntegrationModal';

const Integrations = () => {
    const { t } = useTranslation();
    const account = useSelector(state => state.account?.account);
    const { connect, disconnect, reconnect, manageIntegration, modals } = useIntegrationActions();

    const [isAddModalOpen, setAddModalOpen] = useState(false);

    const { configured, available } = useMemo(() => {
        const dictionary = AccountIntegrationsDictionary.filter(item => isAccountIntegrationAvailable(item, account));

        // One row per connected integration so an account with several accounts of the same type
        // (e.g. two Google or two WhatsApp accounts) shows each connected account on its own row.
        const configuredRows = dictionary.flatMap(item => {
            const matches = account?.integrations?.filter(i => i.type === item.id) ?? [];
            return matches.map(integration => ({
                ...item,
                integrationId: integration.id,
                connected: !!integration.isActive,
                pending: !!integration.isPending,
                hasIntegration: true,
                accountLabel: integration.accountLabel || integration.userIdentifier,
            }));
        });

        // Addable: types with no integration yet, plus multi-account types (to connect another
        // account even when one is already connected).
        const availableItems = dictionary.filter(item => {
            const hasAny = account?.integrations?.some(i => i.type === item.id);
            return !hasAny || item.supportsMultiple;
        });

        return { configured: configuredRows, available: availableItems };
    }, [account]);

    const openAddModal = () => setAddModalOpen(true);

    return (
        <>
            <PageHeader
                icon={<IntegrationsImage />}
                title={t('SettingsIntegrations.headerTitle')}
                subtitle={t('SettingsIntegrations.headerSubtitle')}
                styles={{ marginBottom: 16 }}
            >
                <Button variant="contained" onClick={openAddModal}>
                    {t('SettingsIntegrations.addIntegration')}
                </Button>
            </PageHeader>

            <IntegrationsTable
                integrations={configured}
                onAdd={openAddModal}
                onDisconnect={disconnect}
                onReconnect={reconnect}
                onManageIntegration={manageIntegration}
            />

            {isAddModalOpen ? (
                <AddIntegrationModal
                    isOpen={isAddModalOpen}
                    onClose={() => setAddModalOpen(false)}
                    integrations={available}
                    onConnect={connect}
                />
            ) : null}

            {modals}
        </>
    );
};

export default Integrations;
